import React, { useState, memo } from 'react';
import '../styles/index.css';
import { useArtworksContext } from '../hooks/useArtworksContext';
import { useAuthContext } from '../hooks/useAuthContext';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTrashAlt, faPen } from '@fortawesome/free-solid-svg-icons';
import LazyImage from './LazyImage';
import { axiosInstance } from '../context/AuthContext';
import ArtworkPriceField from './ArtworkPriceField';
import ArtworkEditModal from './ArtworkEditModal';

const ArtworkDetails = ({ artwork, isAdminView, disableNavigation = false }) => {
  const { dispatch } = useArtworksContext();
  const { user } = useAuthContext();
  const [deleteClicked, setDeleteClicked] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleDeleteClick = async () => {
    if (!user) {
      return;
    }

    try {
      const response = await axiosInstance.delete('/artworks/' + artwork._id);
      const json = response.data;
      dispatch({ type: 'DELETE_ARTWORK', payload: json });
      setDeleteClicked(false);
    } catch (error) {
      console.error('Error deleting artwork:', error);
    }
  };

  const toggleDeleteClick = () => {
    if (!deleteClicked) {
      setDeleteClicked(true);
    } else {
      handleDeleteClick();
    }
  };

  const canEditPrice = Boolean(user?.permissions?.includes('admin'));

  const TitleBlock = disableNavigation ? 'div' : Link;
  const titleProps = disableNavigation
    ? { className: 'text-decoration-none' }
    : { to: `/artwork/${artwork._id}`, className: 'text-decoration-none' };
  const ImageBlock = disableNavigation ? 'div' : Link;
  const imageProps = disableNavigation
    ? { className: 'text-decoration-none' }
    : { to: `/artwork/${artwork._id}`, className: 'text-decoration-none' };

  return (
    <div className="w-full max-w-80 mx-auto bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-200">
      <div className="p-4">
        <div className="flex items-center justify-between mb-1">
          <div>
            <TitleBlock {...titleProps}>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg font-sans hover:text-blue-600 transition-colors duration-200">
                  {artwork.title}
                </h3>
                
              </div>
              <p className="text-sm text-gray-500 mb-2 ml-1 flex items-center gap-2">
                by {artwork.artist_email} 
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="w-4 h-4 text-blue-500"
                />
              </p>
            </TitleBlock>
          </div>
          {isAdminView && (
            <div className="flex items-center gap-2 z-10 relative">
              <button
                type="button"
                onClick={() => setShowEditModal(true)}
                className="transition-colors duration-200"
                aria-label="Edit artwork"
              >
                <FontAwesomeIcon
                  icon={faPen}
                  className="w-4 h-4 text-gray-400 hover:text-blue-600"
                />
              </button>
              <button
                type="button"
                onClick={toggleDeleteClick}
                className="transition-colors duration-200"
                aria-label="Delete artwork"
              >
                <FontAwesomeIcon
                  icon={faTrashAlt}
                  className={`w-5 h-5 ${
                    deleteClicked ? 'text-red-700' : 'text-gray-400 hover:text-red-500'
                  }`}
                />
              </button>
            </div>
          )}
        </div>

        <ImageBlock {...imageProps}>
          <div className="relative">
            <LazyImage
              src={artwork.imageBase64}
              alt={`${artwork.title} artwork`}
              className="w-full h-64 object-cover rounded-lg"
              placeholder={<div className="text-gray-400">Loading...</div>}
            />
            <div className="absolute inset-0 bg-black opacity-0 hover:opacity-10 transition-opacity duration-200 rounded-lg"></div>
          </div>

          <div className="flex justify-between text-xs font-sans bg-slate-800 bg-opacity-90 items-center py-4 px-4 rounded-b-lg -mt-2 shadow-md">
            <div className="flex flex-col items-center text-white">
              <span className="font-bold text-base">{artwork.RaspID}</span>
              <span className="text-rose-300 text-xs">Camera ID</span>
            </div>
            <ArtworkPriceField artwork={artwork} canEdit={canEditPrice && !isAdminView} />
            <div className="flex flex-col items-center text-white">
              <span className="font-bold text-base">{artwork.galleryId}</span>
              <span className="text-emerald-300 text-xs">Gallery</span>
            </div>
          </div>
        </ImageBlock>

        {isAdminView && deleteClicked && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 mb-2">Are you sure you want to delete this artwork?</p>
            <div className="flex gap-2">
              <button
                onClick={handleDeleteClick}
                className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
              >
                Confirm Delete
              </button>
              <button
                onClick={() => setDeleteClicked(false)}
                className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {showEditModal && (
        <ArtworkEditModal artwork={artwork} onClose={() => setShowEditModal(false)} />
      )}
    </div>
  );
};

export default memo(ArtworkDetails);
